// Users list component for displaying connected players

import { css, html } from 'lit';
import { getBackendUrl } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import '../shared/token-thumbnail.js';

/**
 * @typedef {object} Player
 * @property {string} id - Player ID
 * @property {string} username - Player username
 * @property {string} avatarUrl - Avatar URL
 * @property {'partyLeader' | 'player' | 'gm'} [role] - Player role
 * @property {'connected' | 'unresponsive' | 'disconnected'} connectionState - Connection state
 */

/**
 * @typedef {object} PlayerCharacter
 * @property {string} id - Character ID
 * @property {string} name - Character name
 * @property {string} [playerId] - Owning player ID
 * @property {object} [token] - Token info
 * @property {string} [token.imageUrl] - Token image URL
 * @property {boolean} [token.hasCircleBorder] - Has circle border
 * @property {string} [token.borderColor] - Border color
 */

/**
 * Users list component
 * Displays connected players and their owned characters
 */
class UsersList extends BaseComponent {
  static properties = {
    players: { type: Array },
    playerCharacters: { type: Array },
    monsterPlacements: { type: Array },
    currentUserId: { type: String, attribute: 'current-user-id' },
    isEditor: { type: Boolean, attribute: 'is-editor' },
  };

  static styles = css`
    :host {
      display: block;
    }

    .users-list {
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    .users-list-header {
      color: white;
      font-size: 18px;
      font-weight: 600;
      margin: 0 0 8px 0;
    }

    .loading-state,
    .empty-state {
      color: #999;
      font-size: 14px;
      text-align: center;
      padding: 20px;
    }

    .users {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .user-item {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      background: #333;
      border: 1px solid #555;
      border-radius: 8px;
      padding: 12px;
      backdrop-filter: blur(4px);
      transition: all 0.2s;
    }

    .user-item:hover {
      background: #444;
      border-color: #666;
    }

    .user-item.connection-connected {
      border-left: 3px solid #22c55e;
    }

    .user-item.connection-unresponsive {
      border-left: 3px solid #eab308;
    }

    .user-item.connection-disconnected {
      border-left: 3px solid #ef4444;
    }

    .user-avatar {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      flex-shrink: 0;
      object-fit: cover;
      border: 2px solid #666;
    }

    .user-info {
      display: flex;
      flex-direction: column;
      gap: 6px;
      flex: 1;
      min-width: 0;
    }

    .user-name-row {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .user-name {
      color: white;
      font-size: 14px;
      font-weight: 500;
    }

    .role-badge {
      display: inline-block;
      padding: 2px 6px;
      background: #8b5cf6;
      color: white;
      font-size: 10px;
      font-weight: 700;
      border-radius: 3px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .user-status {
      display: flex;
      align-items: center;
      gap: 6px;
    }

    .status-indicator {
      width: 8px;
      height: 8px;
      border-radius: 50%;
    }

    .status-indicator.status-connected {
      background: #22c55e;
      box-shadow: 0 0 4px #22c55e;
    }

    .status-indicator.status-unresponsive {
      background: #eab308;
      box-shadow: 0 0 4px #eab308;
    }

    .status-indicator.status-disconnected {
      background: #ef4444;
      box-shadow: 0 0 4px #ef4444;
    }

    .status-text {
      color: #999;
      font-size: 12px;
    }

    .owned-pcs {
      display: flex;
      flex-direction: column;
      gap: 6px;
      margin-top: 4px;
    }

    .pc-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 4px;
      border-radius: 4px;
      transition: background 0.2s;
      position: relative;
    }

    .pc-item-clickable {
      cursor: pointer;
    }

    .pc-item-clickable:hover {
      background: rgba(255, 255, 255, 0.1);
    }

    .pc-navigate-icon {
      margin-left: auto;
      color: #999;
      opacity: 0;
      transition: opacity 0.2s;
    }

    .pc-item-clickable:hover .pc-navigate-icon {
      opacity: 1;
    }

    .pc-name {
      color: #ccc;
      font-size: 13px;
      font-weight: 400;
    }

    .icon {
      width: 16px;
      height: 16px;
    }
  `;

  constructor() {
    super();
    /** @type {Player[]} */
    this.players = [];
    /** @type {PlayerCharacter[]} */
    this.playerCharacters = [];
    /** @type {Array<import('../../types/jsdoc-types.js').MonsterPlacement>} */
    this.monsterPlacements = [];
    this.currentUserId = '';
    this.isEditor = false;
  }

  get _sortedPlayers() {
    return [...this.players].sort((a, b) => {
      // Party Leader always comes first
      if (a.role === 'partyLeader' && b.role !== 'partyLeader') return -1;
      if (a.role !== 'partyLeader' && b.role === 'partyLeader') return 1;
      // Otherwise sort alphabetically by username
      return a.username.localeCompare(b.username);
    });
  }

  /** @param {string} playerId */
  _getOwnedPCs(playerId) {
    return this.playerCharacters.filter((pc) => pc.playerId === playerId);
  }

  /** @param {PlayerCharacter} pc */
  _getTokenImageUrl(pc) {
    const imageUrl = pc.token?.imageUrl || '';
    if (!imageUrl) return '';
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl;
    }
    const backendUrl = getBackendUrl();
    return backendUrl ? backendUrl + imageUrl : imageUrl;
  }

  /** @param {string} state */
  _formatConnectionState(state) {
    switch (state) {
      case 'connected':
        return 'Connected';
      case 'unresponsive':
        return 'Unresponsive';
      case 'disconnected':
        return 'Disconnected';
      default:
        return state;
    }
  }

  /**
   * @param {PlayerCharacter} pc
   * @param {string} playerId
   */
  _handlePCClick(pc, playerId) {
    if (this.isEditor || playerId === this.currentUserId) {
      this.dispatchEvent(new CustomEvent('pc-click', { detail: pc, bubbles: true }));
    }
  }

  _renderChevronRightIcon() {
    return html`
      <svg
        class="icon"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path d="m9 18 6-6-6-6" />
      </svg>
    `;
  }

  render() {
    return html`
      <div class="users-list">
        <h2 class="users-list-header">Users</h2>

        ${
          this._sortedPlayers.length === 0
            ? html` <div class="empty-state">No users found</div> `
            : html`
              <div class="users">
                ${this._sortedPlayers.map(
                  (player) => html`
                    <div class="user-item connection-${player.connectionState}">
                      <img src="${player.avatarUrl}" alt="${player.username}" class="user-avatar" />
                      <div class="user-info">
                        <div class="user-name-row">
                          <span class="user-name">${player.username}</span>
                          ${player.role === 'partyLeader' ? html` <span class="role-badge">Party Leader</span> ` : ''}
                        </div>
                        <div class="user-status">
                          <span class="status-indicator status-${player.connectionState}"></span>
                          <span class="status-text">${this._formatConnectionState(player.connectionState)}</span>
                        </div>
                        ${
                          this._getOwnedPCs(player.id).length > 0
                            ? html`
                              <div class="owned-pcs">
                                ${this._getOwnedPCs(player.id).map(
                                  (pc) => html`
                                    <div
                                      class="pc-item ${
                                        this.isEditor || player.id === this.currentUserId ? 'pc-item-clickable' : ''
                                      }"
                                      @click="${() => this._handlePCClick(pc, player.id)}"
                                    >
                                      <token-thumbnail
                                        image-url="${this._getTokenImageUrl(pc)}"
                                        name="${pc.name}"
                                        ?has-circle-border="${pc.token?.hasCircleBorder || false}"
                                        border-color="${pc.token?.borderColor || '#444'}"
                                        size="32"
                                      ></token-thumbnail>
                                      <span class="pc-name">${pc.name}</span>
                                      ${
                                        this.isEditor || player.id === this.currentUserId
                                          ? html`
                                            <span class="pc-navigate-icon">${this._renderChevronRightIcon()}</span>
                                          `
                                          : ''
                                      }
                                    </div>
                                  `,
                                )}
                              </div>
                            `
                            : ''
                        }
                      </div>
                    </div>
                  `,
                )}
              </div>
            `
        }
      </div>
    `;
  }
}

customElements.define('users-list', UsersList);
